import {
  Controller,
  FilterBuilder,
  useFormContext,
  type FilterBuilderAllowedOperatorsMapping,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  FormSchemaQuestionDisplayType,
  FormSchemaQuestionType,
} from "@validereinc/domain";
import { ComparisonOperators } from "@validereinc/utilities";
import { get } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";
import {
  buildFilterBuilderFieldsFromQuestions,
  denormalizeFilterFromFilterBuilder,
  getNumericFieldKeys,
  hasConditionFields,
  normalizeFilterForFilterBuilder,
} from "./QuestionConditions.helpers";
import styles from "./QuestionConditions.module.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const equalityOperatorValue = "=";
const dataTypeOperatorInputTypeMapping: FilterBuilderAllowedOperatorsMapping = {
  [AttributeDataType.NUMBER]: {
    [equalityOperatorValue]: "number-input",
    [ComparisonOperators.lt]: "number-input",
    [ComparisonOperators.lte]: "number-input",
    [ComparisonOperators.gt]: "number-input",
    [ComparisonOperators.gte]: "number-input",
  },
  [AttributeDataType.INTEGER]: {
    [equalityOperatorValue]: "number-input",
    [ComparisonOperators.lt]: "number-input",
    [ComparisonOperators.lte]: "number-input",
    [ComparisonOperators.gt]: "number-input",
    [ComparisonOperators.gte]: "number-input",
  },
  [AttributeDataType.STRING]: {
    [equalityOperatorValue]: "string-input",
  },
  [AttributeDataType.PICK_LIST]: {
    [ComparisonOperators.in]: "picklist-multi-input",
  },
  "custom-pick-list": {
    [ComparisonOperators.in]: "custom-pick-list-input",
  },
  [AttributeDataType.DATE]: {
    [equalityOperatorValue]: "date-input",
    [ComparisonOperators.eq]: "date-input",
    [ComparisonOperators.lt]: "date-input",
    [ComparisonOperators.lte]: "date-input",
    [ComparisonOperators.gt]: "date-input",
    [ComparisonOperators.gte]: "date-input",
  },
  [AttributeDataType.BOOLEAN]: {
    [equalityOperatorValue]: "boolean-input",
    [ComparisonOperators.eq]: "boolean-input",
    [ComparisonOperators.ne]: "boolean-input",
  },
  mixed: {},
};

export const QuestionConditions = ({
  fieldKey,
  defaultConditions,
  questions,
  isRequired = false,
  onCleared,
}: {
  fieldKey?: string;
  questions: FormSchemaQuestionType[];
  defaultConditions?: FormSchemaQuestionDisplayType["conditions"];
  isRequired?: boolean;
  onCleared?: () => void;
}) => {
  const form = useFormContext<FormSchemaQuestionDisplayType>();

  const filterBuilderFields = useMemo(
    () =>
      buildFilterBuilderFieldsFromQuestions(
        questions as FormSchemaQuestionDisplayType[]
      ),
    [questions]
  );

  const numericFieldKeys = useMemo(
    () => getNumericFieldKeys(filterBuilderFields),
    [filterBuilderFields]
  );

  const normalizedDefaultConditions = useMemo(
    () =>
      normalizeFilterForFilterBuilder(
        (defaultConditions ?? {}) as Record<string, unknown>,
        numericFieldKeys
      ),
    [defaultConditions, numericFieldKeys]
  );

  const [filter, setFilter] = useState(normalizedDefaultConditions);

  const fieldName = fieldKey ? `${fieldKey}conditions` : "conditions";

  return (
    <Controller
      name={fieldName as any}
      control={form.control}
      defaultValue={defaultConditions ?? {}}
      shouldUnregister={false}
      rules={
        isRequired
          ? {
              validate: {
                notEmpty: (value) => {
                  if (!value || typeof value !== "object") {
                    return "At least one condition must be defined";
                  }
                  return (
                    hasConditionFields(value as Record<string, unknown>) ||
                    "At least one condition must be defined"
                  );
                },
              },
            }
          : undefined
      }
      render={({ field }) => (
        <>
          <FilterBuilder
            fields={filterBuilderFields}
            filter={normalizeFilterForFilterBuilder(
              (field.value ?? filter) as Record<string, unknown>,
              numericFieldKeys
            )}
            variant="vertical"
            setFilter={setFilter}
            onFilterChange={(newFilter) => {
              const denormalized = denormalizeFilterFromFilterBuilder(
                newFilter as Record<string, unknown>,
                numericFieldKeys
              );
              const hasFields = hasConditionFields(denormalized);
              const previousHadFields = hasConditionFields(
                (field.value ?? {}) as Record<string, unknown>
              );

              if (!isRequired && previousHadFields && !hasFields) {
                field.onChange({});
                field.onBlur();
                setFilter({});
                onCleared?.();
                return;
              }

              field.onChange(denormalized);
              field.onBlur();
              setFilter(newFilter);
            }}
            maxDepth={1}
            operatorMappingOverride={dataTypeOperatorInputTypeMapping}
          />
          {get(form.formState.errors, fieldName) && (
            <p className={cx("error-message")}>
              {get(form.formState.errors, fieldName ?? "conditions")?.message ??
                ""}
            </p>
          )}
        </>
      )}
    />
  );
};
